import React from "react";
import DocsLayout, { Section, Steps, Callout, JumpNav, AvailableTo } from "./DocsLayout";

const Img = ({ src, alt }: { src: string; alt: string }) => (
  <img src={src} alt={alt} style={{ width: "100%", borderRadius: 8, border: "1px solid #e2e8f0", margin: "16px 0", display: "block" }} />
);

const COLOR = "#1e3a8a";
const ADMIN_ONLY = ["admin"] as const;

export default function AdminSettings() {
  return (
    <DocsLayout>
      <div style={{ marginBottom: 32 }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          background: COLOR + "15", border: `1px solid ${COLOR}30`,
          borderRadius: 8, padding: "5px 14px", marginBottom: 16,
        }}>
          <span style={{ fontSize: 16 }}>⚙️</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: COLOR }}>Admin & Settings</span>
        </div>
        <h1 style={{ fontSize: 30, fontWeight: 800, color: "#0f172a", marginBottom: 8 }}>Admin & Settings</h1>
        <p style={{ fontSize: 15, color: "#64748b", maxWidth: 640, lineHeight: 1.6 }}>
          Company configuration, feature toggles, team/workspace management, and billing — all Admin-only.
        </p>
      </div>

      <JumpNav color={COLOR} items={[
        { id: "onboarding", label: "Company Onboarding" },
        { id: "branding", label: "Branding" },
        { id: "email", label: "Email & Gmail CV Intake" },
        { id: "linkedin", label: "LinkedIn Integration" },
        { id: "billing", label: "Usage & Billing" },
        { id: "clients", label: "Clients" },
        { id: "locale", label: "Currency & Locale" },
        { id: "recruitment-settings", label: "Recruitment Settings" },
        { id: "feature-access", label: "Feature Access" },
        { id: "workspace", label: "Workspace" },
      ]} />

      <Section id="onboarding" title="Company Onboarding (First Login)">
        <AvailableTo roles={[...ADMIN_ONLY]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          On first login, Admin is taken through a 5-step wizard to configure the company. It must be
          completed before any other page is accessible.
        </p>
        <Steps items={[
          "Welcome — overview of the setup steps.",
          "Company — set your company name, logo, employee ID prefix, currency, and locale.",
          "Leaves — define your leave types (Annual, Sick, etc.) with annual allowances and carry-forward rules.",
          "Payroll — configure your standard salary components (Basic, HRA, deductions).",
          "Done — your workspace is ready. You can update all these settings later in Settings.",
        ]} />
      </Section>

      <Section id="branding" title="Branding">
        <AvailableTo roles={[...ADMIN_ONLY]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Settings is the control panel for your HRxAI workspace.
        </p>
        <ul style={{ paddingLeft: 20, color: "#475569", fontSize: 14, lineHeight: 1.9 }}>
          <li>Upload your company logo (shown in the sidebar and on all PDFs)</li>
          <li>Set the company name and employee ID prefix</li>
          <li>Configure locale (timezone, date format) and default currency</li>
        </ul>
      </Section>

      <Section id="email" title="Email & Gmail CV Intake">
        <AvailableTo roles={[...ADMIN_ONLY]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 12 }}>
          Configure your SMTP server so HRxAI can send email notifications (leave approvals, payslips, invites).
        </p>
        <Steps items={[
          "Go to Settings → Email.",
          "Enter SMTP host, port, username, and password.",
          "Send a test email to verify the configuration.",
        ]} />
        <Callout
          icon="💡"
          title="Gmail users"
          body="Use smtp.gmail.com on port 587 with your Gmail address and a Google App Password (not your regular password). Enable 2FA on your Google account first, then generate an App Password at myaccount.google.com/apppasswords."
        />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, margin: "20px 0 12px" }}>
          Separately from SMTP (which only sends mail), you can connect your own Gmail account so HRxAI
          watches that inbox for incoming candidate CVs and creates candidates from them automatically.
        </p>
        <Steps items={[
          "Still on Settings → Email, scroll to Connect Gmail Inbox for CV Intake.",
          "Upload your own Google Cloud OAuth credentials file (client ID/secret) — this keeps your connection fully isolated from other tenants.",
          "Click Connect and complete the Google OAuth consent screen.",
          "Once connected, the page shows the connected Gmail address. Click Disconnect at any time to stop watching that inbox.",
        ]} />
      </Section>

      <Section id="linkedin" title="LinkedIn Integration">
        <AvailableTo roles={[...ADMIN_ONLY]} />
        <Steps items={[
          "Go to Settings → Integrations.",
          "Click Connect LinkedIn and authorise via OAuth.",
          "Once connected, job posts can be published to LinkedIn from the Jobs page.",
        ]} />
      </Section>

      <Section id="billing" title="Usage & Billing">
        <AvailableTo roles={[...ADMIN_ONLY]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 12 }}>
          See your current plan, trial or grace-period status, and how your usage compares to your plan's included
          quotas, and manage billing self-service.
        </p>
        <Img src="/docs/screenshots/admin-settings.png" alt="Usage & Plan page" />
        <Steps items={[
          "Go to Settings → Usage & Plan.",
          "The top card shows your plan and status (Trial / Active / Grace period).",
          "Below, monthly quota bars show usage against each plan limit (employees, CVs processed, AI operations, AI documents, expense captures) — meters with a paid overage option show what you'll be billed if you go over.",
          "On a paid plan, click Manage Billing → to open the Stripe customer portal — update your card, view invoices, or cancel.",
          "To change plans, pick a different plan card and complete checkout.",
        ]} />
        <Callout
          icon="⚠️"
          title="Grace period"
          body="If a payment fails, your account enters a grace period shown here with days remaining. Update your payment method via Manage Billing before it expires to avoid service interruption."
        />
      </Section>

      <Section id="clients" title="Clients">
        <AvailableTo roles={[...ADMIN_ONLY]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 12 }}>
          Manage the list of clients used when creating Invoices and Quotations.
        </p>
        <Steps items={[
          "Go to Settings → Clients.",
          "Click Add Client and fill in their name, contact details, and billing address.",
          "Saved clients appear in the client dropdown when creating an Invoice or Quotation.",
        ]} />
      </Section>

      <Section id="locale" title="Currency & Locale">
        <AvailableTo roles={[...ADMIN_ONLY]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 12 }}>
          Set your company's financial and regional defaults — used across invoices, payroll, and PDFs.
        </p>
        <Steps items={[
          "Go to Settings → Currency & Locale.",
          "Set your default currency and symbol, tax rate and tax label, country, and timezone.",
          "Enter your company's bank details and address — these appear on invoice and receipt PDFs.",
          "Add your tax registration number (and PAN/branch number where applicable), then save.",
        ]} />
      </Section>

      <Section id="recruitment-settings" title="Recruitment Settings">
        <AvailableTo roles={[...ADMIN_ONLY]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 12 }}>
          Maintain a Preferred Companies watch list to help HR and Recruiters spot strong candidates.
        </p>
        <Steps items={[
          "Go to Settings → Recruitment.",
          "Type a company name and press Enter to add it to the watch list.",
          "When a candidate's work history includes a listed company, their profile shows a 🎯 Preferred Company badge.",
        ]} />
      </Section>

      <Section id="feature-access" title="Feature Access">
        <AvailableTo roles={[...ADMIN_ONLY]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Turn individual modules on or off for your whole workspace — useful for hiding features your
          company doesn't use, like Timesheet or Referrals.
        </p>
        <Img src="/docs/screenshots/admin-feature-access.png" alt="Feature Access page" />
        <Steps items={[
          "Go to Settings → Feature Access (or click Feature Access in the sidebar).",
          "Toggle any module on or off for the whole company.",
          "Disabled modules disappear from the sidebar for every role — no per-user override.",
        ]} />
      </Section>

      <Section id="workspace" title="Workspace">
        <AvailableTo roles={["admin", "admin_assistant"]} />
        <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, marginBottom: 16 }}>
          Manage all users in your workspace — invite employees, change roles, and monitor account status.
        </p>
        <Img src="/docs/screenshots/admin-workspace.png" alt="Workspace page" />
        <Steps items={[
          "Click Workspace in the sidebar.",
          "Team tab: see every user, their role, status (Active / Pending / Inactive).",
          "Invite Employee: click Invite and fill in name, email, and role.",
          "Change Role: click the role dropdown next to any user and select a new role.",
          "Resend setup link: for pending users who missed the invite email.",
          "Deactivate: remove access for a user who has left the company.",
        ]} />
        <Callout
          icon="ℹ️"
          title="Available roles"
          body="Employee, HR, Recruiter, Admin Assistant, Finance, Admin. Assign the most restrictive role appropriate for each user."
        />
      </Section>
    </DocsLayout>
  );
}
